import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, inr } from "@/lib/api";
import { Users, Package, ReceiptText, Search } from "lucide-react";
import { Link } from "react-router-dom";

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ customers: [], products: [], invoices: [] });

  useEffect(() => {
    if (!q || q.length < 1) {
      setResults({ customers: [], products: [], invoices: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[GlobalSearch] Search request failed:", e?.message || e);
        }
        setResults({ customers: [], products: [], invoices: [] });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Ctrl+K shortcut
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onOpenChange]);

  const close = () => { onOpenChange(false); setQ(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <DialogTitle className="sr-only">Global Search</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search size={18} className="text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search customers, products, invoices…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="global-search-input"
            className="border-0 focus-visible:ring-0 px-0 h-10"
          />
        </div>
        <div className="max-h-96 overflow-y-auto p-2">
          {results.customers.length === 0 && results.products.length === 0 && results.invoices.length === 0 && q && (
            <div className="text-sm text-muted-foreground p-6 text-center">No results found.</div>
          )}
          {results.customers.length > 0 && (
            <SearchGroup icon={Users} title="Customers" items={results.customers}
              render={(c) => (
                <Link to="/customers" onClick={close} className="flex flex-col">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.phone || c.email}</span>
                </Link>
              )} />
          )}
          {results.products.length > 0 && (
            <SearchGroup icon={Package} title="Products" items={results.products}
              render={(p) => (
                <Link to="/products" onClick={close} className="flex justify-between w-full">
                  <span className="font-medium">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{inr(p.selling_price)}</span>
                </Link>
              )} />
          )}
          {results.invoices.length > 0 && (
            <SearchGroup icon={ReceiptText} title="Invoices" items={results.invoices}
              render={(inv) => (
                <Link to="/invoices" onClick={close} className="flex justify-between w-full">
                  <span className="font-medium">{inv.invoice_number} · {inv.customer_name}</span>
                  <span className="text-xs text-muted-foreground">{inr(inv.grand_total)}</span>
                </Link>
              )} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SearchGroup({ icon: Icon, title, items, render }) {
  return (
    <div className="p-2">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">{title}</div>
      {items.map((it, i) => (
        <div key={it.id || i} className="flex items-center gap-3 px-2 py-2 rounded hover:bg-muted cursor-pointer">
          <Icon size={16} className="text-muted-foreground" />
          <div className="flex-1">{render(it)}</div>
        </div>
      ))}
    </div>
  );
}
